/**
 * OAuth Callback Page
 *
 * Handles OAuth callback from external platforms (Square, QuickBooks, Shopify)
 * Implements OAuth 2.0 PKCE-compliant flow.
 *
 * CRITICAL SECURITY:
 * - ALWAYS validate state parameter before processing
 * - Retrieve code_verifier from sessionStorage (client-side only)
 * - Clear sessionStorage immediately after use
 * - Never expose sensitive data in URLs or logs
 *
 * Flow:
 * 1. Platform redirects to backend /oauth/callback?code=XXX&state=YYY
 * 2. Backend validates state exists, redirects to frontend /oauth/callback?code=XXX&state=YYY
 * 3. Frontend validates state matches sessionStorage
 * 4. Frontend retrieves code_verifier from sessionStorage
 * 5. Frontend calls /oauth/complete with code, state, code_verifier
 * 6. Backend exchanges code for tokens using PKCE
 * 7. Frontend shows success/error and redirects to connections page
 */

import { useEffect, useState, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Result, Button, Spin, Space, Typography, Alert } from 'antd'
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
} from '@ant-design/icons'
import { connectionService } from '@/services/connectionService'
import { TestResultModal } from '@/components/connections'
import type { TestConnectionResult, Platform } from '@/types/connection.types'

const { Text, Paragraph } = Typography

/**
 * OAuth Callback Page
 *
 * Processes OAuth callback from external platforms.
 * Validates CSRF state, retrieves PKCE code_verifier, and completes OAuth flow.
 *
 * @example
 * ```tsx
 * <Route path="/oauth/callback" element={<OAuthCallbackPage />} />
 * ```
 *
 * @example Callback URL from backend
 * ```
 * https://app.rocketpricing.com/oauth/callback?code=abc123&state=csrf_xyz
 * ```
 */
export const OAuthCallbackPage: React.FC = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const [status, setStatus] = useState<'validating' | 'completing' | 'testing' | 'success' | 'error'>('validating')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [connectionName, setConnectionName] = useState<string | null>(null)
  const [testResult, setTestResult] = useState<TestConnectionResult | null>(null)
  const [platform, setPlatform] = useState<Platform | null>(null)
  const [showTestModal, setShowTestModal] = useState(false)

  /**
   * Clear all OAuth-related sessionStorage items
   */
  const clearOAuthSession = useCallback(() => {
    sessionStorage.removeItem('oauth_state')
    sessionStorage.removeItem('oauth_code_verifier')
    sessionStorage.removeItem('oauth_connection_uid')
  }, [])

  useEffect(() => {
    const completeOAuthFlow = async () => {
      // Get query parameters
      const code = searchParams.get('code')
      const state = searchParams.get('state')
      const error = searchParams.get('error')
      const errorDescription = searchParams.get('error_description')

      // Check for errors from platform or backend
      if (error) {
        // Handle user cancellation gracefully - just redirect back to connections
        // This is a normal action, not an error (user might have wanted to re-read something)
        if (error === 'access_denied') {
          clearOAuthSession()
          navigate('/connections?cancelled=true', { replace: true })
          return
        }

        setStatus('error')

        // Handle actual error codes
        switch (error) {
          case 'invalid_state':
            setErrorMessage('Session expired or invalid. Please try connecting again.')
            break
          case 'missing_params':
            setErrorMessage('Invalid callback - missing authorization parameters.')
            break
          case 'callback_failed':
            setErrorMessage('Connection failed. Please try again.')
            break
          default:
            setErrorMessage(errorDescription || `Authorization failed: ${error}`)
        }

        clearOAuthSession()
        return
      }

      // Validate required parameters
      if (!code || !state) {
        setStatus('error')
        setErrorMessage('Invalid callback - missing authorization code or state parameter')
        clearOAuthSession()
        return
      }

      // CRITICAL: Validate CSRF state against sessionStorage
      const storedState = sessionStorage.getItem('oauth_state')

      if (!connectionService.validateOAuthState(state, storedState)) {
        setStatus('error')
        setErrorMessage('Security validation failed. Please try connecting again.')
        clearOAuthSession()
        return
      }

      // CRITICAL: Retrieve PKCE code_verifier from sessionStorage
      const codeVerifier = sessionStorage.getItem('oauth_code_verifier')

      if (!codeVerifier) {
        setStatus('error')
        setErrorMessage('Security error: Missing code verifier. Please try connecting again.')
        clearOAuthSession()
        return
      }

      // State and code_verifier validated - now complete OAuth flow
      setStatus('completing')

      try {
        // Call backend to exchange code for tokens using PKCE
        const response = await connectionService.completeOAuth({
          code,
          state,
          codeVerifier,
        })

        // Clear sessionStorage IMMEDIATELY after use (security)
        clearOAuthSession()

        if (response.success && response.data) {
          setConnectionName(response.data.connectionName)
          setPlatform(response.data.platform)

          // Auto-test the connection to verify it works and get merchant info
          setStatus('testing')

          try {
            const testResponse = await connectionService.test(response.data.connectionUid)

            if (testResponse.success && testResponse.data) {
              // Backend now returns TestConnectionResult directly
              setTestResult(testResponse.data)
            } else {
              // Test failed but connection was saved - still show modal with failure
              setTestResult({
                connectionUid: response.data.connectionUid,
                healthy: false,
                testedAt: new Date().toISOString(),
                responseTimeMs: 0,
                errorMessage: testResponse.errors?.[0]?.message || 'Connection test failed',
              })
            }
          } catch (testErr) {
            // Test error but connection was saved - show modal with generic failure
            console.error('Connection test error:', testErr)
            setTestResult({
              connectionUid: response.data.connectionUid,
              healthy: false,
              testedAt: new Date().toISOString(),
              responseTimeMs: 0,
              errorMessage: 'Unable to verify connection. You can test again later.',
            })
          }

          // Show test result modal
          setStatus('success')
          setShowTestModal(true)
        } else {
          setStatus('error')

          // Extract error message from response
          const errorMsg = response.errors?.[0]?.message || 'Failed to complete connection'
          setErrorMessage(errorMsg)
        }
      } catch (err) {
        // Clear sessionStorage on error too
        clearOAuthSession()

        setStatus('error')
        setErrorMessage('An unexpected error occurred. Please try again.')
        console.error('OAuth complete error:', err)
      }
    }

    // Run the OAuth completion flow
    completeOAuthFlow()
  }, [searchParams, navigate, clearOAuthSession])

  // Validating state
  if (status === 'validating') {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          padding: '24px',
        }}
      >
        <Space direction="vertical" align="center" size="large">
          <Spin
            indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
            size="large"
          />
          <div style={{ textAlign: 'center' }}>
            <Paragraph style={{ fontSize: '16px', marginBottom: '8px' }}>
              Validating authorization...
            </Paragraph>
            <Text type="secondary">Please wait</Text>
          </div>
        </Space>
      </div>
    )
  }

  // Completing OAuth flow
  if (status === 'completing') {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          padding: '24px',
        }}
      >
        <Space direction="vertical" align="center" size="large">
          <Spin
            indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
            size="large"
          />
          <div style={{ textAlign: 'center' }}>
            <Paragraph style={{ fontSize: '16px', marginBottom: '8px' }}>
              Completing connection...
            </Paragraph>
            <Text type="secondary">Securely exchanging credentials</Text>
          </div>
        </Space>
      </div>
    )
  }

  // Testing connection
  if (status === 'testing') {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          padding: '24px',
        }}
      >
        <Space direction="vertical" align="center" size="large">
          <Spin
            indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
            size="large"
          />
          <div style={{ textAlign: 'center' }}>
            <Paragraph style={{ fontSize: '16px', marginBottom: '8px' }}>
              Verifying connection...
            </Paragraph>
            <Text type="secondary">Confirming access to your account</Text>
          </div>
        </Space>
      </div>
    )
  }

  // Success state - show TestResultModal with merchant info
  if (status === 'success') {
    const handleCloseTestModal = () => {
      setShowTestModal(false)
      navigate('/connections', { replace: true })
    }

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          padding: '24px',
        }}
      >
        {/* TestResultModal shows merchant info and test results */}
        <TestResultModal
          open={showTestModal}
          onClose={handleCloseTestModal}
          testResult={testResult}
          platform={platform || 'unknown'}
          isLoading={false}
        />

        {/* Background success message (visible when modal is closed) */}
        <Result
          status="success"
          icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
          title={
            connectionName
              ? `${connectionName} Connected Successfully!`
              : 'Connection Successful!'
          }
          subTitle="Redirecting to connections..."
          extra={[
            <Button
              type="primary"
              key="connections"
              onClick={() => navigate('/connections', { replace: true })}
            >
              View Connections
            </Button>,
          ]}
        />
      </div>
    )
  }

  // Error state
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        padding: '24px',
      }}
    >
      <Result
        status="error"
        icon={<CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
        title="Connection Failed"
        subTitle={errorMessage || 'An unexpected error occurred'}
        extra={[
          <Button
            type="primary"
            key="retry"
            onClick={() => navigate('/connections', { replace: true })}
          >
            Back to Connections
          </Button>,
          <Button
            key="support"
            onClick={() => {
              window.open('https://support.rocketpricing.com', '_blank')
            }}
          >
            Contact Support
          </Button>,
        ]}
      >
        <div style={{ marginTop: '16px' }}>
          <Alert
            message="Common Issues"
            description={
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                <li>Authorization was cancelled or denied</li>
                <li>Platform service temporarily unavailable</li>
                <li>Session expired - try connecting again</li>
              </ul>
            }
            type="warning"
            showIcon
          />
        </div>
      </Result>
    </div>
  )
}

export default OAuthCallbackPage
